import { ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef ,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TaxAttributesService } from './tax-attributes.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { ModalFactoryService } from '../../../global/services/modal-factory.service';
import {
  buildHeaderDataShell,
  firstGridRowObjectFromPayload,
  gridPayloadFromActiveScreen,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { parseAsLocalDate } from '../../../shared/_utils/date-utils';

@Component({
    selector: 'gtw-tax-attributes',
    templateUrl: './tax-attributes.component.html',
    styleUrls: ['./tax-attributes.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class TaxAttributesComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  /** Minimise state for nested NOL modal only (must not toggle main modal `[hidden]`) */
  nestedMinimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = false;

  // Prevent multiple simultaneous requests
  private isLoadingTabs = false;

  /**
   * Skip duplicate tryInit for the same modal+row fingerprint in one session.
   * Cleared on cancel / successful refresh so reopening the modal runs init() again.
   */
  private isInitialized = false;
  private initFingerprint = '';

  // Modal properties (set via modal-name from AngularJS; must not be overwritten in tryInit)
  modalName = '';
  modalTitle = 'Tax Attributes';

  /**
   * Explicit callback name from the `modal-name` attribute that
   * `CeHostDialogComponent` sets on the custom element when the grid opens the
   * dialog. This is the most reliable signal for distinguishing the two modals
   * this component serves (`taxAttribModal` vs `taxAttribEntityModal`), since a
   * single CE tag is registered for both grid callbacks.
   */
  private inputModalName = '';

  @Input('modal-name')
  set _modalName(value: string | null) {
    if (value == null) {
      return;
    }
    const v = String(value).trim();
    // Ignore unresolved AngularJS interpolation passed verbatim as an attribute.
    if (!v || (v.includes('{{') && v.includes('}}'))) {
      return;
    }
    if (this.inputModalName === v) {
      return;
    }
    this.inputModalName = v;
    this.modalName = v;
    // Modal identity changed — re-run initialization for the correct variant.
    this.clearInitLock();
    this.applyWorkspaceContext();
    this.tryInit();
  }

  // Entity data properties
  entityList: any[] = [];

  // Tax attributes data
  taxAttribTabs: any[] = [];
  taxAttribYearsData: any[] = [];
  mergedTaxAttribData: any[] = [];
  activeTab: number = 0;
  trans_type_key: number = 0;

  // Form data
  formDataObj: any = {};
  curentyear: string = '';
  taxAttrsLookUp: boolean = false;

  // Tab management
  tabs = [
    { id: 1, name: 'Tax Attributes' }
  ];

  // Global services data
  globalService: any;
  GENERAL_CONFIG: any;
  USER_SETTINGS: any;
  filterParams: any = {};
  rowData: any = {};

  // Nested modal properties
  showNestedModal: boolean = false;
  selectedRowData: any = {};
  taxAttrib_year_data: any = {};
  scheduled_year: string = '';
  expire_Year: string = '';
  taKey: string = '';

  // Three data arrays for nested modal tables
  allTaxAttribList: any[] = [];           // First table - Tax Attributes with Generated Amount
  taxAttributeYearsData: any[] = [];      // Second table - Years data with Utilized/Pre-Acquisition amounts
  taxAttribEntityTxnData: any[] = [];     // Third table - Entity transaction data

  // Legacy arrays (keeping for compatibility)
  generatedList: any[] = [];
  utilizedList: any[] = [];
  totalList: any[] = [];

  // Transaction types and values
  transValue: string = "1";
  selectedType: string = 'G';
  genValue: string = "Generated";
  disRegTransType: boolean = false;
  disQARTransType: boolean = false;
  noRecords: boolean = false;

  // ----- Tax Attributes Entity modal (taxAttribEntityModal) -----
  loadingEntities = true;
  loadingEntityPostingData = false;
  taAnnualLimit = false;
  activateEntityDropdown = false;
  postingMESelected = false;
  taxAttribEntityData: any[] = [];
  taxAttribEntityMEList: any[] = [];
  taxAttribAnnualLimitData: any[] = [];
  leid = '';
  cdrNo = '';
  rp = '';
  leName = '';
  selectedEntity = '';
  selectedPostingME: string | undefined;
  schdStartYear: number | string | undefined;
  acqDate = '';
  selectedYear = '';
  acqYear = '';
  overrideDateCheck = false;
  disableOverrideDate = true;
  overrideAcquisitionDate: any;
  taxAttribUpdateEntityData: any[] = [];
  targetLeid = '';
  targetCdrNo = '';
  targetRp = '';
  targetleName = '';
  selectedTargetEntity = '';
  taxAttribTargetEntityData: any[] = [];
  selectedME: string | undefined;
  selectedEntity2 = '';
  leid2 = '';
  cdrNo2 = '';
  rp2 = '';
  postingME2 = '';
  entityScheduleMsg: string | undefined;
  /** Entity grid row from `grid-row-data` @Input or nested keys on `row-data` */
  gridRowData: any = null;
  modalData : any = {};
  private readonly subs = new Subscription();

  constructor(
    private _snackBar: MatSnackBar,
    private taxAttributesService: TaxAttributesService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private elementRef: ElementRef<HTMLElement>,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private modalFactory: ModalFactoryService
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    this.globalService = header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);

    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = firstGridRowObjectFromPayload(rawRow);

    this.gridRowData = this.resolveGridRowData(activeScreen);

    this.modalName = this.resolveModalName(activeScreen);

    this.cdr.detectChanges();
  }

  /**
   * Determine which of the two modals this component is serving.
   *
   * A single CE tag (`gtw-tax-attributes-ce`) is registered for both grid
   * callbacks (`taxAttribModal`, `taxAttribEntityModal`). The new app delivers
   * the callback name through `CeHostDialogComponent`, which writes it onto both
   * `activeScreen.data.modalName` / `modal_name` and the `modal-name` attribute
   * (captured by the `@Input` setter). We mirror the canonical lookup used by
   * other migrated multi-callback components (see `ftc-attribute-lock-unlock`).
   */
  private resolveModalName(activeScreen: any): string {
    const resolved =
      this.inputModalName ||
      activeScreen?.data?.modalName ||
      activeScreen?.data?.modal_name ||
      activeScreen?.data?.colData?.call_back ||
      this.modalFactory?.modalFactory?.response?.modalScreen?.modal_name ||
      '';
    // Preserve any previously resolved value (e.g. legacy AngularJS host that
    // embeds the CE without a modal-name) instead of clearing it to empty.
    return resolved || this.modalName;
  }

  /**
   * Mirror the legacy `vemodal.gridRowData = gridData.rowData` contract: the
   * Entity modal shows "Update/Merge Entity" when a grid row drives it and
   * "Create Entity" otherwise. The host bridges the originating grid payload to
   * `activeScreen.data.gridData`, so we read the row from there and normalise an
   * empty/missing payload to `null` (an empty `{}` would be truthy and wrongly
   * flip the Create flow into Update).
   */
  private resolveGridRowData(activeScreen: any): any {
    const gridPayload = gridPayloadFromActiveScreen(activeScreen);
    let gr: any = null;

    if (Array.isArray(gridPayload)) {
      const first = gridPayload[0];
      gr = first?.data ?? first?.rowData ?? first ?? null;
    } else if (gridPayload && typeof gridPayload === 'object') {
      gr = gridPayload.rowData ?? null;
    }

    if (!gr && this.rowData && Object.keys(this.rowData).length > 0) {
      gr = this.rowData;
    }

    return gr && typeof gr === 'object' && Object.keys(gr).length > 0 ? gr : null;
  }

  private computeInitFingerprint(): string {
    const rd = this.rowData || {};
    const gr = this.gridRowData;
    const gridPart =
      gr && typeof gr === 'object'
        ? `${gr.TA_MAST_KEY ?? ''}_${gr.LEID ?? ''}`
        : '';
    return [
      this.modalName,
      rd.TAX_YEAR ?? '',
      rd.LEID ?? '',
      rd.COMBINATION_KEY ?? '',
      gridPart
    ].join('|');
  }

  private clearInitLock(): void {
    this.isInitialized = false;
    this.initFingerprint = '';
    this.taxAttribTabs = [];
  }

  private emitRequestGridUpdate(detail: any, clearInitLockAfter = false): void {
    this.requestGridUpdate.emit(detail);
    if (clearInitLockAfter) {
      this.clearInitLock();
    }
  }

  private tryInit(): void {
    // const fp = this.computeInitFingerprint();
    // if (this.isInitialized && this.initFingerprint === fp) {
    //   return;
    // }
    // if (!this.rowData || Object.keys(this.rowData).length === 0) {
    //   return;
    // }
    if (!this.modalName) {
      // Legacy AngularJS bridge path did not pass modal name.
      // Default to primary Tax Attributes view when context is otherwise valid.
      this.modalName = 'taxAttribModal';
    }
    if (!this.GENERAL_CONFIG || !this.USER_SETTINGS || !this.globalService) {
      return;
    }
    const taxYear =
      this.rowData.TAX_YEAR ||
      this.filterParams.tax_year ||
      this.globalService?.tax_year;
    if (!taxYear) {
      return;
    }
    // this.isInitialized = true;
    // this.initFingerprint = fp;
    this.init();
  }

  ngOnInit(): void {
    this.activeTab = 1;
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /** Binds to split template fragments via `[ctx]="vm"`. */
  get vm(): TaxAttributesComponent {
    return this;
  }

  init() {
    try {
      if (
        // this.rowData &&
        // Object.keys(this.rowData).length > 0 &&
        // this.GENERAL_CONFIG &&
        this.USER_SETTINGS &&
        this.globalService
      ) {
        this.setupEntityData();
        this.setupModalData();

        if (this.modalName === 'taxAttribEntityModal') {
          this.initEntityModal();
          return;
        }

        if (this.taxAttribTabs.length === 0 && this.curentyear) {
          this.fetchModalTabsData();
        }
      }
    } catch (e) {
      console.error('Error in TaxAttributes init:', e);
    }
  }

  private setupEntityData() {
    // 🔴 CRITICAL: Use REAL data from rowData - NO TEST_ values!
    // this.curentyear = this.rowData.TAX_YEAR || this.filterParams.tax_year;
    this.curentyear = this.rowData.TAX_YEAR ||
      this.filterParams.tax_year ||
      this.globalService?.tax_year;

    // Setup form data object
    this.formDataObj = {
      leid: this.rowData.LEID,
      taxCode: this.rowData.CDR_NO,
      rp: this.rowData.REPORTING_PERIOD,
      taxYearEnd: this.rowData.TAX_YEAR_END
    };

    // 🔴 MANDATORY ORDER: Tax Year, LEID, LE Name
    this.entityList = [
      { key: "Tax Year", value: this.curentyear },
      { key: "LEID", value: this.rowData.LEID },
      { key: "LE Name", value: this.rowData.LE_NAME }
    ];
  }

  private setupModalData() {
    // Determine modal type based on data
    if (this.modalName === 'taxAttribEntityModal') {
      // this.modalName = 'taxAttribEntityModal';
      this.modalTitle = this.gridRowData ? 'Update/Merge Entity' : 'Create Entity';
      // Keep template in sync before async entity loads (Update vs Posting tab content)
      this.currentTabName = 'UpdateTab';
    } else {
      // this.modalName = 'taxAttribModal';
      this.modalTitle = 'Tax Attributes';
      this.currentTabName = 'Form Entry';
    }
  }

  private scenarioParam(): string {
    return this.rowData.SCENARIO || this.filterParams.scenario || this.globalService?.scenario;
  }

  private jcdKeyParam(): string | number {
    return this.rowData.JCD_KEY || this.filterParams.jcd_key || this.globalService?.jcd_key || 250;
  }

  // ---------- Entity modal bootstrap & data ----------
  private initEntityModal(): void {
    this.currentTabName = 'UpdateTab';
    this.postingMESelected = false;
    this.taxAttribAnnualLimitData = [];
    this.taAnnualLimit = false;
    this.taxAttribEntityMEList = [];
    this.showFormLoader = true;
    this.loadingEntities = true;
    this.fetchTaxAttribCreateEntityDtls();
  }

  private fetchTaxAttribCreateEntityDtls(): void {
    this.loadingEntities = true;
    const params = {
      action_code: '8t0qhk',
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.curentyear,
      scenario: this.scenarioParam(),
      jcd_key: this.jcdKeyParam()
    };

    this.taxAttributesService
      .loadJsonObject(
        this.GENERAL_CONFIG?.base_url,
        params,
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.taxAttribEntityData = data.jsonObject || [];
          this.loadingEntities = false;
          if (this.gridRowData) {
            this.fetchTaxAttribUpdateEntityDtls();
          } else {
            this.showFormLoader = false;
          }
          this.cdr.detectChanges();
        },
        () => {
          this.loadingEntities = false;
          this.showFormLoader = false;
          this._snackBar.open('Error loading entity list', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cdr.detectChanges();
        }
      );
  }

  setEntityDtls(entity: string): void {
    if (!entity || !this.taxAttribEntityData?.length) {
      return;
    }
    const selected = this.taxAttribEntityData.filter((obj: any) => obj.LEID_DTS === entity);
    if (!selected.length) {
      return;
    }
    const o = selected[0];
    this.leid = o.LEID;
    this.cdrNo = o.CDR_NO;
    this.rp = o.REPORTING_PERIOD;
    this.acqDate = o.ACQ_DATE;
    this.leName = o.LE_NAME;
    this.schdStartYear = o.SCHD_START_YEAR;
    this.selectedYear =
      this.overrideAcquisitionDate != null ? this.overrideAcquisitionDate : o.ACQ_DATE;
    this.acqYear = this.selectedYear
      ? String(this.selectedYear).substring(String(this.selectedYear).lastIndexOf('/') + 1)
      : '';
    this.loadingEntities = false;
    this.selectedME = undefined;
    this.fetchTaxAttribMEEntityList();
    this.fetchTaxAttribAnnualLimitList();
    this.cdr.detectChanges();
  }

  setNewEntityDtls(entity: string): void {
    if (!entity || !this.taxAttribEntityData?.length) {
      return;
    }
    const selected = this.taxAttribEntityData.filter((obj: any) => obj.LEID_DTS === entity);
    if (!selected.length) {
      return;
    }
    const o = selected[0];
    this.leid2 = o.LEID;
    this.cdrNo2 = o.CDR_NO;
    this.rp2 = o.REPORTING_PERIOD;
    this.postingME2 = o.ME_CODE;
    this.postingMESelected = true;
    this.cdr.detectChanges();
  }

  /**
   * The legacy `custom-select-local-system` directive displayed the bound Posting
   * ME value even when the options list was empty (the Update flow never loaded
   * the ME list). A native `<select>` can only show a value that matches an
   * `<option>`, so the templates render a fallback option whenever the selected
   * ME is missing from the loaded list (value === label === ME_CODE).
   */
  selectedPostingMEMissingFromList(): boolean {
    const sel = this.selectedPostingME;
    if (sel === undefined || sel === null || sel === '') {
      return false;
    }
    return !this.taxAttribEntityMEList?.some((m: any) => m.ME_CODE === sel);
  }

  private fetchTaxAttribMEEntityList(): void {
    const params = {
      action_code: 'b6v5lz',
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.curentyear,
      scenario: this.scenarioParam(),
      jcd_key: this.jcdKeyParam(),
      leid: this.leid,
      cdr_no: this.cdrNo,
      reporting_period: this.rp
    };

    this.taxAttributesService
      .loadJsonObject(
        this.GENERAL_CONFIG?.base_url,
        params,
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.taxAttribEntityMEList = data.jsonObject || [];
          if (this.taxAttribEntityMEList.length === 2) {
            this.selectedPostingME = this.taxAttribEntityMEList[0].ME_CODE;
          }
          this.cdr.detectChanges();
        },
        () => {
          console.error('Error loading ME entity list');
        }
      );
  }

  private fetchTaxAttribAnnualLimitList(): void {
    const params = {
      action_code: 'sln1dp',
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.curentyear,
      acq_tax_year: this.acqYear,
      leid: this.leid,
      cdr_No: this.cdrNo,
      reporting_period: this.rp
    };

    this.taxAttributesService
      .loadJsonObject(
        this.GENERAL_CONFIG?.base_url,
        params,
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.taxAttribAnnualLimitData = data.jsonObject || [];
          this.taAnnualLimit = true;
          for (let i = 0; i < this.taxAttribAnnualLimitData.length; i++) {
            const row = this.taxAttribAnnualLimitData[i];
            if (row.amount == null && row.AMOUNT != null) {
              row.amount = row.AMOUNT;
            }
            if (row.trans_type_desc === 'NOL-382') {
              row.disableSrly = true;
              row.disableOverrideDate = true;
              row.overrideDateCheck = false;
              row.calenderOpened = false;
              row.srly_flag = false;
              row.overridedate_flag = false;
              row.acqDate = this.acqDate;
            } else {
              row.disableSrly = false;
              row.calenderOpened = false;
              row.srly_flag = true;
              row.overridedate_flag = true;
              row.acqDate = this.acqDate;
              row.disableAnnualLimit = true;
              if (row.srly_value === 'Y') {
                row.overrideDateCheck = true;
                row.disableOverrideDate = false;
              } else {
                row.overrideDateCheck = false;
                row.disableOverrideDate = true;
              }
              if (row.overridedate_date != null) {
                row.overrideAcquisitionDate = parseAsLocalDate(row.overridedate_date) || '';
              } else {
                row.overridedate_date = '';
              }
            }
          }
          this.cdr.detectChanges();
        },
        () => {
          console.error('Error loading annual limit list');
        }
      );
  }

  changedAnnualLimit(taxAttrib: any): void {
    taxAttrib.isChangedAttrib = true;
  }

  checkAcquisitionDateNew(row: any): void {
    if (row.trans_type_desc === 'NOL-REG') {
      this.disableOverrideDate = !row.overrideDateCheck;
      row.disableOverrideDate = this.disableOverrideDate;
      if (row.disableOverrideDate) {
        row.overrideAcquisitionDate = '';
        row.overridedate_date = '';
      }
    }
  }

  openCalendarRow(row: any): void {
    if (!row.disableOverrideDate) {
      row.calenderOpened = true;
    }
  }

  formatDateRow(row: any): void {
    const v = row.overrideAcquisitionDate;
    if (v instanceof Date) {
      const d = v;
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      row.overridedate_date = `${mm}/${dd}/${yyyy}`;
    } else if (typeof v === 'string' && v.trim() !== '') {
      row.overridedate_date = v;
    }
  }

  /**
   * Value for the Override Date `<input type="date">` (expects `yyyy-MM-dd`).
   * Uses the shared `parseAsLocalDate` util to read the calendar day without
   * any UTC conversion, then formats from local parts — so the displayed day
   * is identical in every timezone (a native date input has no time to shift).
   */
  overrideDateInputValue(row: any): string {
    const raw = row?.overrideAcquisitionDate ?? row?.overridedate_date;
    const d = raw instanceof Date ? raw : parseAsLocalDate(raw);
    if (!d || isNaN(d.getTime())) {
      return '';
    }
    return `${d.getFullYear()}-${this.pad2(d.getMonth() + 1)}-${this.pad2(d.getDate())}`;
  }

  /**
   * Handle a change from the Override Date picker. Persists the picked day as
   * both a local `Date` (UI / legacy parity) and the canonical 'MM/dd/yyyy'
   * string the backend consumes. The shared `parseAsLocalDate` util builds the
   * Date from the literal y/m/d parts, so submission carries the exact day the
   * user selected regardless of the browser timezone (no off-by-one from UTC).
   */
  onOverrideDateChange(row: any, isoValue: string): void {
    const d = parseAsLocalDate(isoValue);
    if (!d) {
      row.overrideAcquisitionDate = '';
      row.overridedate_date = '';
    } else {
      row.overrideAcquisitionDate = d;
      row.overridedate_date = `${this.pad2(d.getMonth() + 1)}/${this.pad2(d.getDate())}/${d.getFullYear()}`;
    }
    this.checkSchdStartYr(row);
  }

  private pad2(n: number): string {
    return String(n).padStart(2, '0');
  }

  checkSchdStartYr(row: any): void {
    this.entityScheduleMsg = undefined;
    let d: Date | null = null;
    if (row.trans_type_desc === 'NOL-REG') {
      if (row.overrideDateCheck) {
        d = parseAsLocalDate(row.overridedate_date);
      } else {
        d = parseAsLocalDate(row.acqDate);
      }
      this.acqYear = d ? String(d.getFullYear()) : this.acqYear;
      if (this.schdStartYear !== undefined && this.schdStartYear !== null && this.schdStartYear !== '') {
        const sy = Number(this.schdStartYear);
        const ay = Number(this.acqYear);
        if (
          isNaN(sy) ||
          String(this.schdStartYear).length < 4 ||
          sy <= 0 ||
          sy > ay
        ) {
          this.entityScheduleMsg = 'Input Date';
        }
      }
    } else {
      const nolReg = this.taxAttribAnnualLimitData.filter(
        (obj: any) => obj.trans_type_desc === 'NOL-REG'
      );
      if (nolReg.length && nolReg[0].overrideDateCheck) {
        d = parseAsLocalDate(nolReg[0].overridedate_date);
      } else if (nolReg.length) {
        d = parseAsLocalDate(nolReg[0].acqDate);
      }
      this.acqYear = d ? String(d.getFullYear()) : this.acqYear;
      if (this.schdStartYear !== undefined && this.schdStartYear !== null && this.schdStartYear !== '') {
        const sy = Number(this.schdStartYear);
        const ay = Number(this.acqYear);
        if (
          isNaN(sy) ||
          String(this.schdStartYear).length < 4 ||
          sy <= 0 ||
          sy > ay
        ) {
          this.entityScheduleMsg = 'Input Date';
        }
      }
    }
  }

  clickEntityTab(tabName: 'update' | 'posting'): void {
    if (tabName === 'update') {
      this.resetEntityModalInner();
      this.currentTabName = 'UpdateTab';
    } else if (tabName === 'posting') {
      this.currentTabName = 'PostingTab';
      if (this.selectedEntity === '') {
        this.activateEntityDropdown = false;
      } else {
        this.fetchEntityList();
        this.fetchPostingEntityDetails();
      }
    }
    this.cdr.detectChanges();
  }

  private resetEntityModalInner(): void {
    if (!this.gridRowData) {
      this.fetchTaxAttribCreateEntityDtls();
      this.leid = '';
      this.cdrNo = '';
      this.rp = '';
      this.leName = '';
      this.selectedEntity = '';
      this.selectedPostingME = undefined;
      this.taxAttribEntityMEList = [];
      this.taAnnualLimit = false;
    } else if (this.gridRowData) {
      this.fetchTaxAttribUpdateEntityDtls();
    }
  }

  fetchPostingEntityDetails(): void {
    if (!this.gridRowData?.TA_MAST_KEY) {
      return;
    }
    const params = {
      action_code: 'uzl0eb',
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.curentyear,
      scenario: this.scenarioParam(),
      jcd_key: this.jcdKeyParam(),
      ta_mast_key: this.gridRowData.TA_MAST_KEY
    };

    this.taxAttributesService
      .loadJsonObject(
        this.GENERAL_CONFIG?.base_url,
        params,
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          const list = data.jsonObject || [];
          if (list.length > 0) {
            this.leid2 = list[0].LEID;
            this.rp2 = list[0].REPORTING_PERIOD;
            this.cdrNo2 = list[0].CDR_NO;
            this.postingME2 = list[0].ME_CODE;
            this.selectedEntity2 = list[0].LEID_DTS;
            if (this.activateEntityDropdown) {
              this.postingMESelected = true;
            }
          }
          this.cdr.detectChanges();
        },
        () => console.error('Error fetchPostingEntityDetails')
      );
  }

  fetchEntityList(): void {
    this.loadingEntityPostingData = true;
    const params = {
      action_code: 'ejsuem',
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.curentyear,
      scenario: this.scenarioParam(),
      jcd_key: this.jcdKeyParam()
    };

    this.taxAttributesService
      .loadJsonObject(
        this.GENERAL_CONFIG?.base_url,
        params,
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.loadingEntityPostingData = false;
          const json = data.jsonObject || [];
          const missing = !json.filter(
            (e: any) =>
              e.LEID === this.leid &&
              e.CDR_NO === this.cdrNo &&
              e.REPORTING_PERIOD === this.rp
          ).length;
          if (missing) {
            this.activateEntityDropdown = true;
            this.taxAttribEntityData = json;
          }
          this.cdr.detectChanges();
        },
        () => {
          this.loadingEntityPostingData = false;
        }
      );
  }

  private fetchTaxAttribUpdateEntityDtls(): void {
    const gr = this.gridRowData;
    if (!gr) {
      this.showFormLoader = false;
      this.cdr.detectChanges();
      return;
    }
    const params = {
      action_code: 'r51uuy',
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.curentyear,
      scenario: this.scenarioParam(),
      jcd_key: this.jcdKeyParam(),
      leid: gr.LEID,
      cdr_No: gr.CDR_NO,
      reporting_period: gr.REPORTING_PERIOD
    };

    this.taxAttributesService
      .loadJsonObject(
        this.GENERAL_CONFIG?.base_url,
        params,
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.taxAttribUpdateEntityData = data.jsonObject || [];
          if (!this.taxAttribUpdateEntityData.length) {
            this.showFormLoader = false;
            this.cdr.detectChanges();
            return;
          }
          const u = this.taxAttribUpdateEntityData[0];
          this.leid = u.LEID;
          this.cdrNo = u.CDR_NO;
          this.rp = u.REPORTING_PERIOD;
          this.leName = u.FULL_NAME;
          this.selectedEntity = u.FULL_NAME;
          this.schdStartYear = u.SCHD_START_YEAR;
          this.acqDate = u.GOLD_ACQDATE;
          this.selectedPostingME = u.ME_CODE;
          this.selectedME = u.ME_CODE;
          this.selectedTargetEntity = u.POSTING_ENTITY;
          this.targetLeid = u.POSTING_LEID;
          this.targetCdrNo = u.POSTING_CDR_NO;
          this.targetRp = u.POSTING_REPORTING_PERIOD;
          this.targetleName = u.POSTING_REPORTING_PERIOD;
          if (u.OVERRIDE_ACQDATE_FLAG === 'Y') {
            this.overrideDateCheck = true;
            this.disableOverrideDate = false;
          } else {
            this.overrideDateCheck = false;
          }
          this.selectedYear =
            u.OVERRIDE_ACQDATE != null ? u.OVERRIDE_ACQDATE : u.GOLD_ACQDATE;
          if (this.selectedYear != null) {
            this.acqYear = String(this.selectedYear).substring(
              String(this.selectedYear).lastIndexOf('/') + 1
            );
          }
          this.overrideAcquisitionDate = u.OVERRIDE_ACQDATE
            ? parseAsLocalDate(u.OVERRIDE_ACQDATE)
            : undefined;
          this.fetchTaxAttribAnnualLimitList();
          // Legacy parity: the Update flow does NOT load the ME options list — it
          // only displays the entity's current ME_CODE (set above as
          // selectedPostingME). The template renders it via a fallback <option>.
          this.showFormLoader = false;
          this.cdr.detectChanges();
        },
        () => {
          this.showFormLoader = false;
          this._snackBar.open('Error loading entity details', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cdr.detectChanges();
        }
      );
  }

  saveEntityModal(): void {
    this.isSaveClicked = true;
    this.crudLoading = true;

    for (let i = 0; i < this.taxAttribAnnualLimitData.length; i++) {
      const row = this.taxAttribAnnualLimitData[i];
      if (row.overrideDateCheck === true) {
        row.srly_value = 'Y';
      } else if (row.overrideDateCheck === false) {
        row.srly_value = 'N';
      }
    }

    if (this.selectedPostingME === undefined || this.selectedPostingME === null || this.selectedPostingME === '') {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this._snackBar.open('Please select a Posting ME', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    if (this.entityScheduleMsg !== undefined) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this._snackBar.open('Please enter a valid Schedule Start Year', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    if (!this.taxAttribAnnualLimitData || this.taxAttribAnnualLimitData.length === 0) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this._snackBar.open('Entity tax attribute details are not loaded. Select an entity and complete the form.', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    const returnObj: any = {
      leid: this.leid,
      cdr_No: this.cdrNo,
      reporting_period: this.rp,
      schdStartYear: this.schdStartYear === undefined ? null : this.schdStartYear,
      posting_meCode: this.selectedPostingME,
      posting_leid: this.targetLeid,
      posting_cdrNo: this.targetCdrNo,
      posting_rp: this.targetRp,
      transType: this.taxAttribAnnualLimitData,
      entity_dml_type: !this.gridRowData ? 'I' : 'U'
    };

    const returnClobSettingsObj = {
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.curentyear,
      scenario: this.scenarioParam(),
      jcd_key: this.jcdKeyParam(),
      leid: this.leid,
      cdr_No: this.cdrNo,
      reporting_period: this.rp
    };

    this.taxAttributesService
      .saveTaxAttributesEntityDtls(
        this.GENERAL_CONFIG?.base_url,
        returnClobSettingsObj,
        [returnObj],
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (result: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          const err = result.errorMessage || result.data?.errorMessage;
          if (err && err !== 'null') {
            this._snackBar.open(err, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('Tax Attributes has been successfully saved/updated', 'Close', {
              duration: 4000,
              panelClass: ['success-snackbar']
            });
            this.emitRequestGridUpdate('refresh', true);
          }
          this.cdr.detectChanges();
        },
        () => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cdr.detectChanges();
        }
      );
  }

  savePostingEntity(): void {
    this.crudLoading = true;
    const returnClobSettingsObj = {
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.curentyear,
      scenario: this.scenarioParam(),
      jcd_key: this.jcdKeyParam()
    };
    const returnObj = {
      leid: this.leid,
      cdr_No: this.cdrNo,
      reporting_period: this.rp,
      posting_meCode: this.selectedPostingME,
      new_leid: this.leid2,
      new_cdr_No: this.cdrNo2,
      new_reporting_period: this.rp2,
      new_posting_meCode: this.postingME2
    };

    this.taxAttributesService
      .savePostingEntity(
        this.GENERAL_CONFIG?.base_url,
        returnClobSettingsObj,
        returnObj,
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (result: any) => {
          this.crudLoading = false;
          const err = result.errorMessage || result.data?.errorMessage;
          const ok =
            result.callSuccess === '1' ||
            result.data?.callSuccess === '1' ||
            (!err || err === 'null');
          if (err && err !== 'null') {
            this._snackBar.open(err, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else if (ok) {
            this._snackBar.open('Posting Entity Saved!', 'Close', {
              duration: 4000,
              panelClass: ['success-snackbar']
            });
            this.emitRequestGridUpdate('refresh', true);
          } else {
            this._snackBar.open('Error occurred while saving posting entity.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
          this.cdr.detectChanges();
        },
        () => {
          this.crudLoading = false;
          this._snackBar.open('Error occurred while saving posting entity.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.cdr.detectChanges();
        }
      );
  }

  fetchModalTabsData() {
    // Prevent multiple simultaneous requests
    if (this.isLoadingTabs) {
      return;
    }

    // Check if we have valid tax_year
    if (!this.curentyear) {
      return;
    }

    this.isLoadingTabs = true;
    this.showFormLoader = true;

    const params = {
      action_code: 'o482i5',
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.curentyear
    };

    this.taxAttributesService.loadJsonObject(
      this.GENERAL_CONFIG?.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.showFormLoader = false;
        this.isLoadingTabs = false;

        if (response && response.jsonObject && response.jsonObject.length > 0) {
          this.taxAttribTabs = response.jsonObject;
          if (this.taxAttribTabs.length > 0) {
            this.trans_type_key = this.taxAttribTabs[0].TRANS_TYPE_KEY;
            this.activeTab = this.trans_type_key;
            this.fetchModalScheduledYearsData();
          }
        }
      },
      (error: any) => {
        this.showFormLoader = false;
        this.isLoadingTabs = false;
        console.error('Error fetching modal tabs data:', error);
        this._snackBar.open('Error loading tax attributes data', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  fetchModalScheduledYearsData(taxAttribObj?: any) {
    this.showFormLoader = true;

    if (taxAttribObj) {
      this.trans_type_key = taxAttribObj.TRANS_TYPE_KEY;
      this.activeTab = this.trans_type_key;
    }

    const params = {
      action_code: 'x1kh73',
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.curentyear,
      scenario: this.rowData.SCENARIO || this.filterParams.scenario,
      jcd_key: this.rowData.JCD_KEY || this.filterParams.jcd_key,
      ta_mast_key: this.rowData.TA_MAST_KEY,
      trans_type_key: this.trans_type_key
    };

    this.taxAttributesService.loadJsonObject(
      this.GENERAL_CONFIG?.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.showFormLoader = false;
        if (response && response.jsonObject) {
          this.taxAttribYearsData = response.jsonObject;
          if (this.taxAttribYearsData.length > 0) {
            this.taKey = this.taxAttribYearsData[0].TA_KEY; // Store TA_KEY for nested modals
          }
          this.fetchMergedEntitiesData();
        }
      },
      (error: any) => {
        this.showFormLoader = false;
        console.error('Error fetching scheduled years data:', error);
        this._snackBar.open('Error loading scheduled years data', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  fetchMergedEntitiesData() {
    const params = {
      action_code: '4sr7n4',
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.curentyear,
      trans_type_key: this.trans_type_key,
      leid: this.rowData.LEID,
      cdr_no: this.rowData.CDR_NO,
      reporting_period: this.rowData.REPORTING_PERIOD
    };

    this.taxAttributesService.loadJsonObject(
      this.GENERAL_CONFIG?.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          this.mergedTaxAttribData = response.jsonObject;
        }
      },
      (error: any) => {
        console.error('Error fetching merged entities data:', error);
      }
    );
  }

  onTabClick(tab: any) {
    this.activeTab = tab.TRANS_TYPE_KEY;
    this.fetchModalScheduledYearsData(tab);
  }

  openPopUpWindow(row: any) {
    this.openNestedModal(row);
  }

  private openNestedModal(row: any) {
    this.selectedRowData = row;
    this.nestedMinimise = false;
    this.showNestedModal = true;
    this.fetchPopUpData(row);
  }

  private fetchPopUpData(row: any) {
    this.taxAttrib_year_data = row;
    this.scheduled_year = row.SCHD_TAX_YEAR;

    if (this.taxAttrib_year_data.TAX_YEAR != null) {
      if (this.taxAttrib_year_data.EXPIRE_YEAR == '-') {
        this.taxAttrib_year_data.EXPIRE_YEAR = '';
        this.expire_Year = '';
      } else {
        this.expire_Year = this.taxAttrib_year_data.EXPIRE_YEAR;
      }
    } else {
      this.expire_Year = this.taxAttrib_year_data.DUMMY_EXPIRE_YEAR;
    }

    // Fetch all three data sets for the nested modal
    this.fetchAllTaxAttribList();
    this.fetchTaxYearsAmounts();
    this.fetchTaxAttribEntityTxn();
  }

  // Fetch first table data - Tax Attributes (allTaxAttribList)
  private fetchAllTaxAttribList() {
    // Build params exactly like AngularJS fetchPopUpData (com87r)
    const params = {
      action_code: 'com87r',
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      attrib_tax_year: this.scheduled_year,  // 🔴 Use scheduled_year
      scenario: this.rowData.SCENARIO || this.filterParams.scenario || this.globalService?.scenario,
      jcd_key: this.rowData.JCD_KEY || this.filterParams.jcd_key || this.globalService?.jcd_key,
      ta_key: this.taKey,
      expire_year: this.expire_Year,
      trans_type_key: this.activeTab
    };

    this.taxAttributesService.loadJsonObject(
      this.GENERAL_CONFIG?.base_url, params, this.globalService, this.filterParams, this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          // Filter like AngularJS: NOL_TYPE : ctrl.transValue, TRANS_TYPE_KEY : ctrl.current_tab
          this.allTaxAttribList = response.jsonObject.filter((item: any) =>
            item.NOL_TYPE === this.transValue && item.TRANS_TYPE_KEY === this.activeTab
          );

          // Keep legacy arrays for compatibility
          this.generatedList = response.jsonObject.filter((item: any) => item.UTIL_TYPE === "G");
          this.utilizedList = response.jsonObject.filter((item: any) => item.UTIL_TYPE === "U");
          this.totalList = response.jsonObject;
        }
      },
      (error: any) => {
        console.error('Error fetching all tax attrib list:', error);
      }
    );
  }

  // Fetch second table data - Tax Years Amounts (taxAttributeYearsData)
  private fetchTaxYearsAmounts() {
    // Build params exactly like AngularJS fetchTaxYearsAmounts
    const params = {
      action_code: 've2xn6',
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.scheduled_year,  // 🔴 Use scheduled_year NOT filterParams.tax_year
      scenario: this.rowData.SCENARIO || this.filterParams.scenario || this.globalService?.scenario,
      jcd_key: this.rowData.JCD_KEY || this.filterParams.jcd_key || this.globalService?.jcd_key,
      ta_key: this.allTaxAttribList.length > 0 ? this.allTaxAttribList[0].TA_KEY : this.taKey,
      trans_type_key: this.activeTab
    };

    this.taxAttributesService.loadJsonObject(
      this.GENERAL_CONFIG?.base_url, params, this.globalService, this.filterParams, this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          this.taxAttributeYearsData = response.jsonObject;
        }
      },
      (error: any) => {
        console.error('Error fetching tax years amounts:', error);
      }
    );
  }

  // Fetch third table data - Tax Attrib Entity Transaction (taxAttribEntityTxnData)
  private fetchTaxAttribEntityTxn() {
    // Build params exactly like AngularJS fetchTaxAttribEntityTxn
    const params = {
      action_code: 'orruzh',
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      scheduled_tax_year: this.scheduled_year,  // 🔴 Use scheduled_year
      trans_type_key: this.activeTab,
      leid: this.rowData.LEID,
      cdr_no: this.rowData.CDR_NO,
      reporting_period: this.rowData.REPORTING_PERIOD
    };

    this.taxAttributesService.loadJsonObject(
      this.GENERAL_CONFIG?.base_url, params, this.globalService, this.filterParams, this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          // Process the data like in AngularJS
          for (let i = 0; i < response.jsonObject.length; i++) {
            response.jsonObject[i]['UTIL_TYPE'] = response.jsonObject[i].T_TYPE.split('|')[0];
            response.jsonObject[i]['TRANS_TYPE'] = response.jsonObject[i].T_TYPE.split('|')[1];
          }
          this.taxAttribEntityTxnData = response.jsonObject;
        }
      },
      (error: any) => {
        console.error('Error fetching tax attrib entity txn:', error);
      }
    );
  }

  deleteUtilAmount(row: any) {
    if (confirm('Are you sure to delete the utilized amount?')) {
      this.isSaveClicked = true;
      this.crudLoading = true;

      const deleteData = {
        sso_id: this.USER_SETTINGS?.user?.sso_id,
        ta_key: row.TA_KEY,
        trans_key: row.TRANS_KEY,
        ref_trans_key: row.REF_TRANS_KEY
      };

      const deleteDetails = [{
        ta_key: row.TA_KEY,
        trans_key: row.TRANS_KEY,
        ref_trans_key: row.REF_TRANS_KEY
      }];

      this.taxAttributesService.deleteUtilDtls(
        this.GENERAL_CONFIG?.base_url,
        deleteData,
        deleteDetails,
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (response: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;

          if (response.errorMessage && response.errorMessage !== 'null') {
            this._snackBar.open(response.errorMessage, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('Utilized amount has been successfully deleted', 'Close', {
              duration: 4000,
              panelClass: ['success-snackbar']
            });
            this.emitRequestGridUpdate('refresh', true);
          }
        },
        (error: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this._snackBar.open('Error occurred while deleting. Please try again.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
      );
    }
  }

  save() {
    if (this.modalName === 'taxAttribEntityModal') {
      if (this.currentTabName === 'PostingTab') {
        return;
      }
      this.saveEntityModal();
      return;
    }
  }

  reset() {
    if (this.modalName === 'taxAttribEntityModal') {
      this.resetEntityModalInner();
      return;
    }
    this.fetchModalScheduledYearsData();
  }

  cancel(): void {
    this.ngZone.run(() => {
      this.clearInitLock();
      this.emitRequestGridUpdate('cancel', false);
      this.minimise = false;
      this.nestedMinimise = false;
      this.cdr.detectChanges();
    });
  }

  toggleMinimise(event?: MouseEvent): void {
    event?.stopPropagation();
    this.ngZone.run(() => {
      this.minimise = !this.minimise;
      this.cdr.detectChanges();
    });
  }

  toggleNestedMinimise(event?: MouseEvent): void {
    event?.stopPropagation();
    this.ngZone.run(() => {
      this.nestedMinimise = !this.nestedMinimise;
      this.cdr.detectChanges();
    });
  }

  // Nested modal methods
  closeNestedModal(): void {
    this.ngZone.run(() => {
      this.showNestedModal = false;
      this.nestedMinimise = false;
      this.selectedRowData = {};

      this.allTaxAttribList = [];
      this.taxAttributeYearsData = [];
      this.taxAttribEntityTxnData = [];

      this.totalList = [];
      this.generatedList = [];
      this.utilizedList = [];
      this.cdr.detectChanges();
    });
  }

  changeTAAmount(changedTA: any) {
    changedTA['showErrors'] = false;
    if (changedTA.UTIL_TYPE == 'G') {
      changedTA.AMOUNT = Math.abs(changedTA.AMOUNT);
      changedTA.PREACQUI_AMT = Math.abs(changedTA.PREACQUI_AMT);
    } else {
      if (isNaN(changedTA.AMOUNT)) {
        changedTA.AMOUNT = 0;
      }
      if (isNaN(changedTA.PREACQUI_AMT)) {
        changedTA.PREACQUI_AMT = 0;
      }
      changedTA.AMOUNT = -Math.abs(changedTA.AMOUNT);
      changedTA.PREACQUI_AMT = -Math.abs(changedTA.PREACQUI_AMT);
      if (changedTA.ORIGINAL_AVAILABLE_AMT == null || changedTA.ORIGINAL_AVAILABLE_AMT == undefined) {
        changedTA['ORIGINAL_AVAILABLE_AMT'] = changedTA.AVAILABLE_AMT;
      }
      changedTA.AVAILABLE_AMT = (changedTA.ORIGINAL_AVAILABLE_AMT + changedTA.AMOUNT + changedTA.PREACQUI_AMT);
      if (changedTA.AVAILABLE_AMT < 0) {
        changedTA['showErrors'] = true;
      }
    }
    changedTA['taAmountChange'] = true;
  }

  saveNestedModal() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Legacy validation 1: utilized amount can't exceed available amount.
    const errorSaveData = this.taxAttributeYearsData.filter(
      (item: any) => item.TRANS_TYPE === 'REG' && item.showErrors === true
    );
    if (errorSaveData.length > 0) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this._snackBar.open("Please make sure utilized amount can't exceed available amount", 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Legacy validation 2: for the 382 limitation tab (current_tab == 73) the sum of
    // utilized amounts can't exceed the rolling annual (382) limitation amount.
    if (this.activeTab == 73) {
      let totalSum = 0;
      for (const yearRow of this.taxAttributeYearsData) {
        if (yearRow.AMOUNT != undefined) {
          totalSum += parseInt(yearRow.AMOUNT, 10);
        }
      }
      totalSum = Math.abs(totalSum);
      if (totalSum > this.taxAttrib_year_data.ROLLING_ANNUAL_LIMIT) {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open("Sum of Utilized amount can't be greater than 382 Limitation Amount", 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        return;
      }
    }

    const changedFromAllTaxAttrib = this.allTaxAttribList.filter(item => item.taAmountChange === true);

    const changedFromTaxYears = this.taxAttributeYearsData.filter(item => item.taAmountChange === true);
    const allChangedItems = [...changedFromAllTaxAttrib, ...changedFromTaxYears];

    const changedItemsMap = new Map();

    const getItemKey = (item: any): string => {
      return `${item.TA_KEY || ''}_${item.TRANS_KEY || ''}_${item.REF_TRANS_KEY || ''}_${item.UTIL_TYPE || ''}`;
    };

    allChangedItems.forEach(item => {
      const key = getItemKey(item);
      changedItemsMap.set(key, item);
    });

    const saveData = Array.from(changedItemsMap.values());

    if (saveData.length === 0) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this._snackBar.open('No changes to save', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    // Build clob_data exactly like legacy: assign values verbatim. Missing fields
    // (undefined) are dropped by JSON.stringify, matching the original AngularJS payload.
    const taxAttributesSaveDtls = saveData.map(item => ({
      ta_mast_key: item.TA_MAST_KEY,
      ta_key: item.TA_KEY,
      trans_type_key: this.activeTab,
      ref_trans_key: item.REF_TRANS_KEY,
      trans_key: item.TRANS_KEY,
      // Legacy parity: util_type is 'G' only for generated rows, otherwise forced
      // to 'U'. The year rows (taxAttributeYearsData) carry no UTIL_TYPE, so passing
      // it through verbatim would drop the key and break the backend insert/update.
      util_type: item.UTIL_TYPE === 'G' ? item.UTIL_TYPE : 'U',
      expire_year: item.UTIL_TYPE === 'G' ? this.taxAttrib_year_data.NOL_EXPIRE_YEAR : item.EXPIRE_YEAR,
      amount: item.AMOUNT,
      preacqui_amt: item.PREACQUI_AMT,
      reg_qar: this.transValue,
      comments: item.DETAILREASON
    }));

    const clobSettings = {
      sso_id: this.USER_SETTINGS?.user?.sso_id,
      tax_year: this.scheduled_year,
      jcd_key: this.rowData.JCD_KEY || this.filterParams.jcd_key || this.globalService?.jcd_key || 250,
      leid: this.rowData.LEID,
      cdr_No: this.rowData.CDR_NO,
      ta_mast_key: saveData[0].TA_MAST_KEY,
      reporting_period: this.rowData.REPORTING_PERIOD,
      trans_type: this.transValue
    };

    this.taxAttributesService.saveTaxAttributes(
      this.GENERAL_CONFIG?.base_url,
      clobSettings,
      taxAttributesSaveDtls,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Tax Attributes has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.closeNestedModal();
          // Refresh main data
          this.fetchModalScheduledYearsData();
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  resetNestedModal() {
    // Reset to original data
    this.fetchPopUpData(this.selectedRowData);
    this.transValue = "1";
    this.genValue = "Generated";
    this.selectedType = 'G';
  }
}
